import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const navItems = [
  { path: '/', labelKey: 'nav.home', icon: 'ri-home-4-line' },
  { path: '/category/dianying', labelKey: 'nav.movies', icon: 'ri-film-line' },
  { path: '/category/dianshiju', labelKey: 'nav.tvShows', icon: 'ri-tv-line' },
  { path: '/category/zongyi', labelKey: 'nav.variety', icon: 'ri-live-line' },
  { path: '/category/dongman', labelKey: 'nav.anime', icon: 'ri-rocket-line' },
  { path: '/about', labelKey: 'nav.about', icon: 'ri-information-line' },
];

export default function Navbar() {
  const { t } = useTranslation();
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  const isActive = useCallback(
    (path: string) => {
      if (path === '/') return location.pathname === '/';
      return location.pathname.startsWith(path);
    },
    [location.pathname],
  );

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/70'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          <Link
            to="/"
            className="flex items-center gap-2 text-foreground-900 hover:text-primary-500 transition-colors duration-200 whitespace-nowrap"
          >
            <div className="w-9 h-9 flex items-center justify-center">
              <i className="ri-movie-2-line text-2xl text-primary-500"></i>
            </div>
            <span className="font-heading text-xl font-bold tracking-wide">
              {t('site.name')}
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`relative px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                  isActive(item.path)
                    ? 'text-primary-500 bg-primary-50'
                    : 'text-foreground-700 hover:text-foreground-900 hover:bg-background-100'
                }`}
              >
                {t(item.labelKey)}
                {isActive(item.path) && (
                  <span className="absolute bottom-1 left-1/2 -translate-x-1/2 w-5 h-0.5 bg-primary-500 rounded-full"></span>
                )}
              </Link>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <div className="relative group">
              <div className="flex items-center gap-2 px-4 py-2 bg-background-100 rounded-full border border-background-200/70 transition-all duration-200 group-focus-within:border-primary-300 group-focus-within:bg-background-50">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-search-line text-sm text-foreground-400"></i>
                </div>
                <input
                  type="text"
                  placeholder={t('site.search')}
                  className="bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none w-36 focus:w-48 transition-all duration-200"
                />
              </div>
            </div>
          </div>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors"
            aria-label="Toggle menu"
          >
            <div className="w-5 h-5 flex items-center justify-center">
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </div>
          </button>
        </div>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70">
          <div className="px-4 py-3">
            <div className="flex items-center gap-2 px-4 py-2.5 bg-background-100 rounded-full border border-background-200/70 mb-3">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-search-line text-sm text-foreground-400"></i>
              </div>
              <input
                type="text"
                placeholder={t('site.search')}
                className="bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none flex-1"
              />
            </div>
            <nav className="flex flex-col gap-0.5">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActive(item.path)
                      ? 'text-primary-500 bg-primary-50'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${item.icon} text-base`}></i>
                  </div>
                  {t(item.labelKey)}
                </Link>
              ))}
            </nav>
          </div>
        </div>
      )}
    </header>
  );
}